import { observer } from "mobx-react-lite"
import Layout from "../../components/layout/Layout"
import { useStore } from "../../stores/root-store-context";
import { Button, Flex, Form, Input, Popconfirm, Image, Spin, Table, TableProps, Tag, message, AutoComplete, Popover } from "antd";
import { useEffect, useState } from "react";
import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { TextSort } from "../../helpers/SorterHelper";
import { useTranslation } from "react-i18next";
import { getUserComponent } from "../../helpers/user";
import { AddJobTitle } from "./AddJobTitle";
import { AddNewUser } from "./AddNewUser";
import { AddBulkEmail } from "./AddBulkEmail";
import { MailReloadIcon } from "../../components/customIcons/MailReloadIcon";
import { Roles } from "../../enums/Roles";
import { Employee } from "../../models/Employee";

const Search = Input.Search;

interface DataType {
    key: string;
    name: string;
    age: number;
    address: string;
    tags: string[];
}

const Users = observer(() => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { applicationStore, employeesStore } = useStore();
    const [searchText, setSearchText] = useState("");
    const [filteredUser, setFilteredUser] = useState<Employee[]>([]);

    const [selectedUser, setSelectedUser] = useState("");
    const [expandedSkills, setExpandedSkills] = useState<{ [key: string]: boolean }>({});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOpenBulkEmail, setIsOpenBulkEmail] = useState(false);
    const [isModalJobTitle, setIsModalJobTitle] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const hideModal = () => {
        form.resetFields();
        setIsModalOpen(false);
    };

    const showBulkEmail = () => {
        setIsOpenBulkEmail(true);
    };

    const hideBulkEmail = () => {
        form.resetFields();
        setIsOpenBulkEmail(false);
    };

    const showModalJobTitle = () => {
        setIsModalJobTitle(true);
    };

    const hideModalJobTitle = () => {
        setIsModalJobTitle(false);
    };

    const getUserName = (userId: string) => {
        const user = employeesStore.users?.find(el => el.id === userId);
        if (user) {
            return `${user.firstName} ${user.lastName}`
        } else {
            return ""
        }
    };

    const columns: TableProps<Employee>['columns'] = [
        {
            title: `${t("user.Name")}`,
            dataIndex: 'photoId',
            width: 200,
            key: 'photo',
            render: (data, record) => <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, record, applicationStore.company?.Tenant)}</div>,
            sorter: (a: Employee, b: Employee) => {
                const lastNameCompare = TextSort(a.lastName, b.lastName);
                if (lastNameCompare !== 0) return lastNameCompare;

                return TextSort(a.firstName, b.firstName);
            },
            defaultSortOrder: 'ascend'
        },
        {
            title: `${t("ReportingManager")}`,
            dataIndex: 'chiefId',
            width: 200,
            key: 'immediateChief',
            render: (immediateChief, record) => {
                if (applicationStore.user?.roles?.some(r => r.id === Roles.Owner || r.id === Roles.Admin || r.id === Roles.Financier) || applicationStore.user.id === record.id) {
                    return (
                        <Flex gap={5} align="center">
                            <AutoComplete
                                style={{ height: 40, width: 150 }}
                                placeholder={t("ReportingManager")}
                                value={getUserName(immediateChief)}
                                onChange={(e, option: any) => {
                                    // form.setFieldValue("chiefId", option?.id)
                                    record.chiefId = option?.id;
                                    employeesStore.editUser({
                                        id: record.id,
                                        chiefId: option?.id
                                    }).then(() => {
                                        employeesStore.getUsers().catch(console.log)
                                    })
                                }}
                                options={employeesStore.users?.filter(user => user.id !== record.id).map((el, index) => ({
                                    value: el.id,
                                    id: el.id, label: <Flex align="center">
                                        <Image
                                            preview={false}
                                            width={40}
                                            height={40}
                                            style={{ borderRadius: "50%", objectFit: "cover", minWidth: 40 }}
                                            src={el.photoId ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${el.photoId}` :
                                                `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`
                                            }
                                        />
                                        <Flex vertical justify="start" gap={5} style={{ marginLeft: 20 }}>
                                            <div>{el.firstName + " " + el.lastName}</div>
                                            <div>{el.email}</div>
                                        </Flex>
                                    </Flex>
                                }))}
                            />
                            <Button disabled={!(applicationStore.user?.roles?.some(r => r.id === Roles.Owner || r.id === Roles.Admin) || applicationStore.user.id === record.id)}
                                onClick={() => {
                                    employeesStore.editUser({
                                        id: record.id,
                                        chiefId: -1
                                    }).then(() => {
                                        employeesStore.getUsers().catch(console.log)
                                    })
                                }}
                                type="primary" danger shape="circle" icon={<DeleteOutlined />} />
                        </Flex>
                    )
                } else if (applicationStore.user?.roles?.some(r => r.id === Roles.User)) {
                    return (
                        <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, employeesStore.users.find(el => el.id === immediateChief), applicationStore.company?.Tenant, 20)}</div>
                    )
                } else {
                    return (
                        <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, employeesStore.users.find(el => el.id === immediateChief), applicationStore.company?.Tenant, 20)}</div>
                    )
                }
            },
            sorter: (a: Employee, b: Employee) => TextSort(a.jobTitleId || "", b.jobTitleId || "")
        },
        {
            title: `${t("user.JobTitle")}`,
            dataIndex: 'jobTitleId',
            width: 100,
            key: 'jobTitle',
            render: (jobTitleId, record) => {
                if (applicationStore.user?.roles?.some(r => r.id === Roles.Owner || r.id === Roles.Admin || r.id === Roles.Financier)) {
                    return (
                        <>
                            <AutoComplete
                                key={"length" + employeesStore.jobTitles.length}
                                value={employeesStore.jobTitles.find(el => el.id === jobTitleId)?.title}
                                style={{ minWidth: 120 }}
                                placeholder={`${t("user.JobTitle")}`}
                                options={[...employeesStore.jobTitles?.map((el, index) => ({
                                    key: el.id,
                                    value: el.title,
                                    label: el.title,
                                    data: el
                                })), {
                                    label: <Button onClick={() => {
                                        setSelectedUser(record.id);
                                        showModalJobTitle();
                                    }}>{t("CreateJobTitle")}</Button>,
                                }]}

                                onSelect={(value, option: any) => {
                                    if (option?.value) {
                                        employeesStore.editUser({
                                            id: record.id,
                                            jobTitleId: option?.key
                                        }).then(() => {
                                            employeesStore.getUsers().catch(console.log)
                                        })
                                    }
                                }}
                            />
                        </>
                    );
                }
                const jobTitle = employeesStore.jobTitles.find(el => el.id === jobTitleId)?.title;

                return <div>{jobTitle || t(" ")}</div>;
            },
            sorter: (a: Employee, b: Employee) => TextSort(a.jobTitleId || "", b.jobTitleId || "")
        },
        {
            title: `${t("CompanyRole")}`,
            dataIndex: 'roles',
            width: 70,
            key: 'role',
            render: (roles: { id: number; name: string }[], record) => {
                const isOwner = roles?.some(r => r.id === Roles.Owner);
                const isCurrentUserOwner = applicationStore.user?.roles?.some(r => r.id === Roles.Owner);
                const isCurrentUserManager = applicationStore.user?.roles?.some(r => r.id === Roles.Manager);
                const isCurrentUserFinancier = applicationStore.user?.roles?.some(r => r.id === Roles.Financier);
                const isCurrentUserAdmin = applicationStore.user?.roles?.some(r => r.id === Roles.Admin);
                const canManageRoles = !isOwner && (
                    isCurrentUserOwner ||
                    isCurrentUserAdmin ||
                    isCurrentUserFinancier
                );

                const getAvailableRoles = () => {
                    if (isCurrentUserOwner) {
                        return Object.entries(Roles).filter(([key, value]) =>
                            !isNaN(Number(value)) &&
                            Number(value) !== Roles.Owner
                        );
                    } else if (isCurrentUserManager || isCurrentUserFinancier) {
                        return Object.entries(Roles).filter(([key, value]) =>
                            !isNaN(Number(value)) &&
                            [Roles.Manager, Roles.User, Roles.Viewer].includes(Number(value))
                        );
                    }
                    return [];
                };

                return (
                    <Flex wrap gap={8} align="center">
                        {roles?.map(role => (
                            <Tag
                                key={role.id}
                                color={role.id === Roles.Owner ? 'gold' : 'blue'}
                                closable={canManageRoles && roles.length > 1}
                                onClose={(e) => {
                                    e.preventDefault();
                                    const newRoles = roles.filter(r => r.id !== role.id);
                                    employeesStore.editUser({
                                        id: record.id,
                                        userRoles: newRoles.map(r => r.id)
                                    }).then(() => employeesStore.getUsers());
                                }}
                            >
                                {t(Roles[role.id])}
                            </Tag>
                        ))}

                        {canManageRoles && (
                            <Popover
                                trigger="click"
                                content={
                                    <Flex vertical gap={8}>
                                        {getAvailableRoles()
                                            .filter(([key, value]) =>
                                                !roles?.some(r => r.id === Number(value))
                                            )
                                            .map(([key, value]) => (
                                                <Button
                                                    key={value}
                                                    type="text"
                                                    onClick={() => {
                                                        const newRoles = [...(roles || []), {
                                                            id: Number(value),
                                                            name: t(Roles[Number(value)])
                                                        }];
                                                        employeesStore.editUser({
                                                            id: record.id,
                                                            userRoles: newRoles.map(r => r.id)
                                                        }).then(() => employeesStore.getUsers());
                                                    }}
                                                >
                                                    {t(key)}
                                                </Button>
                                            ))
                                        }
                                    </Flex>
                                }
                            >
                                <Button
                                    type="dashed"
                                    size="small"
                                    icon={<PlusOutlined />}
                                >
                                    {t("AddRole")}
                                </Button>
                            </Popover>
                        )}
                    </Flex>
                );
            },
            sorter: (a: Employee, b: Employee) => {
                const getRolePriority = (roles: { id: number }[]) => {
                    return roles?.find(r => r.id === Roles.Owner)?.id ||
                        roles?.reduce((min, role) => Math.min(min, role.id), Infinity) ||
                        0;
                };

                const priorityA = getRolePriority(a.roles);
                const priorityB = getRolePriority(b.roles);

                return TextSort(priorityA.toString(), priorityB.toString());
            }
        },
        {
            title: `${t("Skills")}`,
            dataIndex: 'userSkills',
            key: 'userSkills',
            width: 70,
            render: (skills, project) => <Flex wrap style={{ maxWidth: 200 }} gap={10}>
                {(expandedSkills[project.id] ? skills : skills.slice(0, 5)).map((el: any) => (
                    <Tag color={el.isConfirmed ? "green" : "blue"} key={el.skill.title}>{el.skill.title}</Tag>
                ))}
                {skills?.length > 5 && (
                    expandedSkills[project.id] ? (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: false
                        }))} style={{ cursor: 'pointer' }} color="blue">{t("ShowLess")}</Tag>
                    ) : (
                        <Tag onClick={() => setExpandedSkills(prevState => ({
                            ...prevState,
                            [project.id]: true
                        }))} style={{ cursor: 'pointer' }} color="blue">+{skills.length - 5} more</Tag>
                    )
                )}
            </Flex>,
            sorter: (a: Employee, b: Employee) => TextSort(a.firstName, b.firstName),
            filters: applicationStore.skills?.map(skill => { return { text: skill.title, value: skill.skillId } }),
            onFilter: (value, record) => record.userSkills.some(el => el.skill.skillId === value),


        },
        {
            title: '',
            dataIndex: 'operation',
            width: 100,
            render: (_: any, record: Employee) => {
                const canManage = !!applicationStore.user?.roles?.some(r =>
                    [Roles.Owner, Roles.Admin, Roles.Financier].includes(r.id)
                );
                if (record.isApproveInvite === false) {
                    return (
                        <Flex justify="space-between" gap={10}>
                            <Popconfirm
                                title={t("ResendTheInvitation")}
                                description={t("SendNewEmailInvite")}
                                okText={t("Yes")}
                                cancelText={t("No")}
                                onConfirm={async () => {
                                    try {
                                        const firstRole = record.roles?.[0];
                                        if (!firstRole) throw new Error("No role assigned");
                                        employeesStore.setLoading(true);
                                        await employeesStore.resendInvite(
                                            record.email,
                                            Roles[firstRole.id] as keyof typeof Roles,
                                            record.chiefId ?? '',
                                            record.jobTitleId
                                        );
                                        message.success(t("InvitationSuccessfullyResend"));
                                    } catch (err) {
                                        message.error(t("FailedResend"));
                                    } finally {
                                        employeesStore.setLoading(false);
                                    }
                                }}
                            >
                                <Button
                                    type="primary"
                                    shape="circle"
                                    disabled={!canManage}
                                    icon={<MailReloadIcon />}
                                    title={t("ResendInvite")}
                                />
                            </Popconfirm>

                            {/* DELETE PERMANENTLY */}
                            {canManage && (
                                <Popconfirm
                                    title={t("DeleteUser")}
                                    description={t("AreYouSureToDeleteUser")}
                                    okText={t("Yes")}
                                    cancelText={t("No")}
                                    onConfirm={async () => {
                                        try {
                                            if (typeof (employeesStore as any).deleteUserPermanently === 'function') {
                                                await (employeesStore as any).deleteUserPermanently(record.id);
                                            } else {
                                                await employeesStore.deleteUser(record.id);
                                            }
                                            message.success(t("UserDeletedSuccessfully"));
                                        } catch {
                                            message.error(t("FailedToDeleteUser"));
                                        }
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        danger
                                        shape="circle"
                                        icon={<DeleteOutlined />}
                                        title={t("DeleteUser")}
                                    />
                                </Popconfirm>
                            )}
                        </Flex>
                    );
                }

                return (
                    <Flex justify="space-between" gap={10}>
                        <Button
                            type="primary"
                            shape="circle"
                            onClick={() => navigate(`./${record.id}`)}
                            icon={<EyeOutlined />}
                        />
                        <Popconfirm
                            title={t("DeleteUser")}
                            description={t("AreYouSureToDeleteUser")}
                            okText={t("Yes")}
                            cancelText={t("No")}
                            onConfirm={async () => {
                                try {
                                    await employeesStore.deleteUser(record.id);
                                    message.success(t("UserDeletedSuccessfully"));
                                } catch {
                                    message.error(t("FailedToDeleteUser"));
                                }
                            }}
                        >
                            {canManage && (
                                <Button
                                    type="primary"
                                    danger
                                    shape="circle"
                                    icon={<DeleteOutlined />}
                                />
                            )}
                        </Popconfirm>
                    </Flex>
                );
            },
        }

    ];

    useEffect(() => {
        if (employeesStore.allUsers.length) {
            setFilteredUser(employeesStore.allUsers)
            console.log("Filtered users 1:", filteredUser);
        } else {
            employeesStore.getUsers().then(res => {
                setFilteredUser(employeesStore.allUsers)
                console.log("Filtered users 2:", filteredUser);
            })
        }
    }, [employeesStore.allUsers])

    useEffect(() => {
        let data = employeesStore.allUsers;
        if (searchText.length > 0) {
            data = data.filter(el =>
                el.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
                el.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
                el.email?.toLowerCase().includes(searchText.toLowerCase())
            );
        }
        data = data.filter(el => el.isActive !== false);
        data = data.map(el => { return { ...el, key: el.id } });
        setFilteredUser(data);
        console.log("Filtered users 3:", filteredUser);
    }, [employeesStore.allUsers, searchText]);

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={`${t("Employers")}`}>
            <div className="container" style={{ margin: "20px auto" }}>
                <Spin spinning={employeesStore.isLoading || applicationStore.isLoading || filteredUser.length === 0}>
                    {applicationStore.isFreezeModalVisible ? (
                        <p style={{ padding: '20px', textAlign: 'center' }}>{t("AccessUsersLimited")}</p>
                    ) : (
                        <>
                            <Flex className="users-header" justify="space-between" style={{ marginBottom: 10 }}>
                                <Search style={{ width: 300 }} onChange={(e) => {
                                    setSearchText(e.target.value);
                                }} />
                                <div>
                                    <Flex className="users-action-buttons" justify="flex-end" gap={10}>
                                        {applicationStore.user?.roles?.some(r =>
                                            [Roles.Owner, Roles.Manager, Roles.Admin, Roles.Financier].includes(r.id)
                                        ) && (
                                                <>
                                                    <Button type="primary" onClick={showModal}>
                                                        {t("AddEmployee")}
                                                    </Button>
                                                    <Button type="primary" onClick={showBulkEmail}>
                                                        {t("SendBulkEmail")}
                                                    </Button>
                                                </>
                                            )}
                                        <Button onClick={() => navigate(`resource`)} type="primary">
                                            {t("Resource")}
                                        </Button>
                                        <Button onClick={() => navigate(`organogram`)} type="primary">
                                            {t("OrganizationalChart")}
                                        </Button>
                                    </Flex>
                                </div>
                            </Flex>
                            <Table
                                columns={columns}
                                dataSource={filteredUser.filter(user => user.isActive !== false)}
                                scroll={{ x: 1200 }}
                                onRow={(record) => ({
                                    style: record.isApproveInvite === false ? {
                                        backgroundColor: '#c6c6c6',
                                        cursor: "not-allowed",
                                        pointerEvents: 'stroke'
                                    } : {},
                                })}
                            />
                        </>
                    )}
                </Spin>
            </div>
            <AddNewUser isModalOpen={isModalOpen} hideModal={hideModal} />
            <AddBulkEmail isModalOpen={isOpenBulkEmail} hideModal={hideBulkEmail} />
            <AddJobTitle isModalOpen={isModalJobTitle} hideModal={hideModalJobTitle} userId={selectedUser} />
        </Layout>
    )
})
export default Users;